import { toApiUrl } from '../config/apiClient';
import { resolvePostAuthReturnUrl, sanitizeReturnUrl } from './authRedirect';

export const X_AUTH_COMPLETION_STORAGE_KEY = 'insideinjuries.xAuthCompletion';

const X_AUTH_SOURCES = new Set(['login', 'signup', 'popup']);

const X_AUTH_ERROR_MESSAGES = {
	access_denied: 'X sign-in was canceled. Please try again if you still want to continue.',
	authorization_failed: 'X sign-in could not be completed. Please try again.',
	cancelled: 'X sign-in was canceled. Please try again if you still want to continue.',
	invalid_link: 'This X sign-in link is no longer valid. Please start again.',
	missing_exchange_code: 'The X sign-in response was incomplete. Please start again.',
	expired_completion: 'Your X sign-in session expired. Please start again.',
	completion_required: 'Please finish setting up your account to continue.',
	server_error: 'X sign-in failed. Please try again later.'
};

export function normalizeXAuthSource(source = 'login') {
	return X_AUTH_SOURCES.has(source) ? source : 'login';
}

export function buildXAuthStartUrl(returnUrl, source = 'login') {
	const params = new URLSearchParams();
	const safeReturnUrl = sanitizeReturnUrl(returnUrl);

	if (safeReturnUrl) {
		params.set('returnUrl', safeReturnUrl);
	}

	params.set('source', normalizeXAuthSource(source));

	return `${toApiUrl('/auth/x/start')}?${params.toString()}`;
}

export function parseXCallbackParams(search = '') {
	const params = new URLSearchParams(search || '');

	return {
		exchangeCode: (params.get('exchange_code') || '').trim(),
		error: (params.get('error') || '').trim(),
		returnUrl: resolvePostAuthReturnUrl(null, search)
	};
}

export function getXAuthErrorMessage(codeOrMessage, fallbackMessage = 'X sign-in failed. Please try again.') {
	if (typeof codeOrMessage !== 'string') {
		return fallbackMessage;
	}

	const trimmedValue = codeOrMessage.trim();
	if (!trimmedValue) {
		return fallbackMessage;
	}

	const normalizedKey = trimmedValue.toLowerCase();
	if (X_AUTH_ERROR_MESSAGES[normalizedKey]) {
		return X_AUTH_ERROR_MESSAGES[normalizedKey];
	}

	return trimmedValue;
}

export function storeXCompletionState(state) {
	if (typeof window === 'undefined') {
		return;
	}

	window.sessionStorage.setItem(X_AUTH_COMPLETION_STORAGE_KEY, JSON.stringify(state || {}));
}

export function loadXCompletionState() {
	if (typeof window === 'undefined') {
		return null;
	}

	const rawValue = window.sessionStorage.getItem(X_AUTH_COMPLETION_STORAGE_KEY);
	if (!rawValue) {
		return null;
	}

	try {
		const parsedValue = JSON.parse(rawValue);
		if (!parsedValue || typeof parsedValue !== 'object') {
			return null;
		}

		return parsedValue;
	} catch (error) {
		return null;
	}
}

export function clearXCompletionState() {
	if (typeof window === 'undefined') {
		return;
	}

	window.sessionStorage.removeItem(X_AUTH_COMPLETION_STORAGE_KEY);
}

export function buildXCompleteUrl(returnUrl, verification = false) {
	const params = new URLSearchParams();
	const safeReturnUrl = sanitizeReturnUrl(returnUrl);

	if (safeReturnUrl) {
		params.set('returnUrl', safeReturnUrl);
	}

	if (verification) {
		params.set('verification', '1');
	}

	const queryString = params.toString();
	return `/auth/x/complete${queryString ? `?${queryString}` : ''}`;
}

export function appendReturnUrl(pathname, returnUrl) {
	const safeReturnUrl = sanitizeReturnUrl(returnUrl);
	if (!safeReturnUrl) {
		return pathname;
	}

	return `${pathname}?returnUrl=${encodeURIComponent(safeReturnUrl)}`;
}
